import React from "react";
import { nanoid } from "nanoid";
import { useTranslation } from "react-i18next";
import {
  Stack,
  Typography,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  IconButton,
} from "@mui/material";
import SubActionButton from "@quest_editor/components/Buttons/SubActionButton";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ClearIcon from "@mui/icons-material/Clear";
import {
  ProductWithManufacturer,
  productsWithManufacturer,
} from "@quest_editor/mocks";

const TargetProductField = ({
  targetProductRows,
  setTargetProductRows,
  emptyLabel,
}: {
  targetProductRows: ProductWithManufacturer[];
  setTargetProductRows: React.Dispatch<
    React.SetStateAction<ProductWithManufacturer[]>
  >;
  emptyLabel: string;
}) => {
  const { t } = useTranslation("questEditor");
  const [barcodes, setBarcodes] = React.useState<string>("");

  const handleRemoveTargetProduct = (id: string) => {
    setTargetProductRows((prev: ProductWithManufacturer[]) =>
      prev.filter((row) => row.id !== id),
    );
  };

  const handleAddTargetProduct = () => {
    if (!barcodes.trim()) return;

    const barcodeList = barcodes
      .split(/[,\r\n]+/)
      .map((b) => b.trim())
      .filter(Boolean);

    const foundProducts: ProductWithManufacturer[] = barcodeList
      .map((barcode) => {
        const found = productsWithManufacturer.find(
          (p) => p.barcode === barcode,
        );
        if (found) {
          return { ...found, id: nanoid() };
        }
        return null;
      })
      .filter(
        (p: ProductWithManufacturer | null): p is ProductWithManufacturer =>
          p !== null,
      );

    setTargetProductRows((prev: ProductWithManufacturer[]) => {
      const existingBarcodes = new Set(prev.map((p) => p.barcode));
      const uniqueNew = foundProducts.filter(
        (p) => !existingBarcodes.has(p.barcode),
      );
      return [...prev, ...uniqueNew];
    });
    setBarcodes("");
  };

  return (
    <Accordion
      sx={{
        ".MuiAccordionSummary-root .MuiSvgIcon-root": {
          transform: "rotate(0deg)",
        },
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        {targetProductRows.length > 0 ? (
          <Typography color="primary">
            {targetProductRows.map((product) => product.name).join(", ")}
          </Typography>
        ) : (
          <Typography color="primary">{emptyLabel}</Typography>
        )}
      </AccordionSummary>
      <AccordionDetails>
        <Stack direction="column" divider={<Divider />} spacing={1}>
          {targetProductRows.map((row: ProductWithManufacturer) => (
            <Stack
              key={row.id}
              direction="row"
              alignItems="center"
              spacing={4}
            >
              <Typography sx={{ color: "text.secondary", flexGrow: 1 }}>
                {row.name}
              </Typography>
              <Typography sx={{ color: "text.secondary", flexGrow: 1 }}>
                {row.manufacturer.name}
              </Typography>
              <Typography sx={{ color: "text.secondary", flexGrow: 1 }}>
                {row.barcode}
              </Typography>
              <IconButton onClick={() => handleRemoveTargetProduct(row.id)}>
                <ClearIcon />
              </IconButton>
            </Stack>
          ))}
          <Stack direction="row" alignItems="center" spacing={1}>
            <TextField
              size="small"
              placeholder={t("common_field.target_product.placeholder")}
              value={barcodes}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setBarcodes(e.target.value)
              }
              multiline
              rows={4}
              sx={{ flexGrow: 1 }}
            />
            <SubActionButton
              onClick={handleAddTargetProduct}
              sx={{ whiteSpace: "nowrap" }}
            >
              {t("common_field.target_product.button")}
            </SubActionButton>
          </Stack>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

export default TargetProductField;
